import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { formatDateTime, formatTimestamp } from "@/lib/format";
import { AdminLivePanel } from "@/components/AdminLivePanel";
import { DriverWalletDialog } from "@/components/DriverWalletDialog";
import {
  Truck, LogOut, Radio, IndianRupee, CheckCircle2, XCircle,
  ArrowRight, Clock, Megaphone, TrendingUp, UserCheck, Loader2,
  FileText, FileWarning, Car, Plus, Trash2, Save, Package, X, Image as ImageIcon, Pencil, Star,
} from "lucide-react";

const inr = (n) => (n == null ? "—" : "₹" + Number(n).toLocaleString("en-IN"));
const PAX_OPTIONS = ["2", "3", "4", "5", "6", "12"];
const RENTAL_PACKAGES = ["4hr / 40km", "8hr / 80km", "12hr / 120km"];
const API_ROOT = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLE = {
  new: "bg-slate-100 text-slate-700 border-slate-200",
  priced: "bg-amber-100 text-amber-700 border-amber-200",
  broadcast: "bg-indigo-100 text-indigo-700 border-indigo-200",
  accepted: "bg-emerald-100 text-emerald-700 border-emerald-200",
};
const DRIVER_STATUS_STYLE = {
  pending: "bg-amber-100 text-amber-700 border-amber-200",
  approved: "bg-emerald-100 text-emerald-700 border-emerald-200",
  rejected: "bg-red-100 text-red-700 border-red-200",
};

export default function AdminPanel() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [leads, setLeads] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [stats, setStats] = useState({});
  const [vtypes, setVtypes] = useState([]);
  const [priceEdits, setPriceEdits] = useState({});
  const [fareEdits, setFareEdits] = useState({});
  const [vtEdits, setVtEdits] = useState({});
  const [newVt, setNewVt] = useState({ name: "", base_fare: "", per_km_rate: "", round_trip_per_km: "", round_trip_per_hour: "", night_halt_rate: "" });
  const [itins, setItins] = useState([]);
  const [newItin, setNewItin] = useState({ kind: "cab_package", title: "", destination: "", region: "", summary: "", cover_photo_path: null, price_from: "", days: [], inclusions: [], exclusions: [], pax_rates: {} });
  const [editingItinId, setEditingItinId] = useState(null);
  const [hillRates, setHillRates] = useState([]);
  const [rateEdits, setRateEdits] = useState({});
  const [newRate, setNewRate] = useState({ from: "", to: "", rates: {} });
  const [newDriver, setNewDriver] = useState({ name: "", phone: "", email: "", city: "", state: "", vehicle_type: "" });
  const [customers, setCustomers] = useState([]);
  const [newCustomer, setNewCustomer] = useState({ name: "", phone: "", email: "" });
  const [expandedCust, setExpandedCust] = useState(null);
  const [editCust, setEditCust] = useState(null);
  const [custEdit, setCustEdit] = useState({ name: "", phone: "", email: "" });
  const [enqCust, setEnqCust] = useState(null);
  const [highlightCust, setHighlightCust] = useState(null);
  const [enqForm, setEnqForm] = useState({ from_location: "", to_location: "", travel_date: "", travel_time: "", vehicle_type: "", fare_offer: "", trip_type: "One Way", stopovers: "" });
  const [editingCity, setEditingCity] = useState(null);
  const [cityValue, setCityValue] = useState("");
  const [busy, setBusy] = useState({});
  const [walletDriver, setWalletDriver] = useState(null);

  const load = useCallback(async () => {
    const [l, d, s, v, it, hr, cu] = await Promise.allSettled([
      api.get("/admin/leads"),
      api.get("/admin/drivers"),
      api.get("/admin/stats"),
      api.get("/admin/vehicle-types"),
      api.get("/admin/itineraries"),
      api.get("/admin/hill-rates"),
      api.get("/admin/customers"),
    ]);
    if (l.status === "fulfilled") setLeads(l.value.data);
    if (d.status === "fulfilled") setDrivers(d.value.data);
    if (s.status === "fulfilled") setStats(s.value.data);
    if (v.status === "fulfilled") setVtypes(v.value.data);
    if (it.status === "fulfilled") setItins(it.value.data);
    if (hr.status === "fulfilled") setHillRates(hr.value.data);
    if (cu.status === "fulfilled") setCustomers(cu.value.data);
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [load]);

  const doLogout = async () => { await logout(); navigate("/"); };

  const savePrice = async (id) => {
    const val = priceEdits[id];
    if (!val || Number(val) <= 0) return toast.error("Enter a valid lead price");
    const fare = fareEdits[id];
    setBusy({ ...busy, [id]: "price" });
    try {
      const payload = { lead_price: Number(val) };
      if (fare && Number(fare) > 0) payload.suggested_fare = Number(fare);
      await api.put(`/admin/leads/${id}/price`, payload);
      toast.success(payload.suggested_fare ? "Lead price & suggested fare saved" : "Lead price saved");
      await load();
    } catch (e) { toast.error("Could not save price"); }
    finally { setBusy({ ...busy, [id]: null }); }
  };

  const broadcast = async (id, toAll = false) => {
    const lead = leads.find((x) => x.id === id);
    if (lead?.targeted && !window.confirm("This enquiry was sent to specific drivers. Broadcasting will send it to all matching drivers instead. Continue?")) return;
    const force = lead?.verified === false;
    if (force && !window.confirm("This customer has not verified yet. Send to drivers anyway?")) return;
    setBusy({ ...busy, [id]: "broadcast" });
    try {
      const res = await api.post(`/admin/leads/${id}/broadcast`, { to_all: toAll, force });
      const n = res.data.drivers_notified;
      const cfg = res.data.notify?.configured;
      toast.success(`Broadcast sent to ${n} ${toAll ? "" : "matching "}driver(s).` + (cfg && n ? " SMS/WhatsApp dispatched." : n ? " (in-app only)" : ""));
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Broadcast failed"); }
    finally { setBusy({ ...busy, [id]: null }); }
  };

  const addDriver = async () => {
    if (!newDriver.name || !newDriver.phone || !newDriver.city || !newDriver.vehicle_type)
      return toast.error("Name, phone, city and vehicle are required");
    setBusy({ ...busy, add_driver: true });
    try {
      const cities = newDriver.city.split(",").map((c) => c.trim()).filter(Boolean);
      const r = await api.post("/admin/drivers", { ...newDriver, cities });
      toast.success(r.data.existing ? `Driver already registered (+91 ${r.data.phone}) — details updated` : "Driver added & approved");
      setNewDriver({ name: "", phone: "", email: "", city: "", state: "", vehicle_type: "" });
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add driver"); }
    finally { setBusy({ ...busy, add_driver: false }); }
  };

  const saveDriverCity = async (d) => {
    if (!cityValue.trim()) return toast.error("Enter a city");
    setBusy({ ...busy, [`city_${d.user_id}`]: true });
    try {
      await api.put(`/admin/drivers/${d.user_id}`, { city: cityValue.trim() });
      toast.success("Cities updated");
      setEditingCity(null);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update city"); }
    finally { setBusy({ ...busy, [`city_${d.user_id}`]: null }); }
  };

  const addCustomer = async () => {
    if (!newCustomer.name || !newCustomer.phone)
      return toast.error("Name and phone are required");
    setBusy({ ...busy, add_customer: true });
    try {
      const r = await api.post("/admin/customers", newCustomer);
      if (r.data.existing) {
        toast.info(`Already registered: ${r.data.name || "Customer"} · +91 ${r.data.phone || "—"} (matched by ${r.data.matched_on})`);
        setHighlightCust(r.data.user_id);
        setExpandedCust(r.data.user_id);
      } else {
        toast.success("Customer added");
        setNewCustomer({ name: "", phone: "", email: "" });
      }
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add customer"); }
    finally { setBusy({ ...busy, add_customer: false }); }
  };

  const saveCustomer = async (c) => {
    setBusy({ ...busy, [`cust_${c.user_id}`]: true });
    try {
      await api.put(`/admin/customers/${c.user_id}`, custEdit);
      toast.success("Customer updated");
      setEditCust(null);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update"); }
    finally { setBusy({ ...busy, [`cust_${c.user_id}`]: null }); }
  };

  const createEnquiry = async (c) => {
    if (!enqForm.from_location || !enqForm.to_location) return toast.error("Enter from & to locations");
    if (!enqForm.travel_date) return toast.error("Select travel date");
    if (!enqForm.vehicle_type) return toast.error("Select vehicle type");
    setBusy({ ...busy, [`enq_${c.user_id}`]: true });
    try {
      await api.post(`/admin/customers/${c.user_id}/enquiry`, {
        ...enqForm,
        fare_offer: enqForm.fare_offer ? Number(enqForm.fare_offer) : null,
        stopovers: enqForm.stopovers ? enqForm.stopovers.split(",").map((s) => s.trim()).filter(Boolean) : [],
      });
      toast.success("Enquiry created — set price & broadcast from the Leads tab");
      setEnqForm({ from_location: "", to_location: "", travel_date: "", travel_time: "", vehicle_type: "", fare_offer: "", trip_type: "One Way", stopovers: "" });
      setEnqCust(null);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create enquiry"); }
    finally { setBusy({ ...busy, [`enq_${c.user_id}`]: null }); }
  };

  // mirrors broadcast_lead routing: approved, has phone, vehicle match, ANY city (>=2 chars) substring of pickup
  const broadcastReach = (lead) => drivers.filter((d) => {
    if (!(d.driver_status === "approved" && d.phone)) return false;
    if (!lead.premium && d.vehicle_type !== lead.vehicle_type) return false;
    const pickup = (lead.from_location || "").toLowerCase();
    const dcities = [...(d.cities || []), d.city].filter(Boolean).map((c) => c.trim().toLowerCase());
    return dcities.some((c) => c.length >= 2 && pickup.includes(c));
  }).length;
  const approvedDrivers = drivers.filter((d) => d.driver_status === "approved" && d.phone).length;

  const togglePremium = async (l) => {
    setBusy({ ...busy, [`prem_${l.id}`]: true });
    try {
      await api.put(`/admin/leads/${l.id}/premium`, { premium: !l.premium });
      toast.success(l.premium ? "Premium removed" : "Marked Premium — goes to all drivers in the pickup city, first buyer gets it exclusively");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update"); }
    finally { setBusy({ ...busy, [`prem_${l.id}`]: null }); }
  };

  const setDriverStatus = async (uid, status) => {
    setBusy({ ...busy, [uid]: status });
    try {
      await api.put(`/admin/drivers/${uid}/status`, { status });
      toast.success(`Driver ${status}`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
    finally { setBusy({ ...busy, [uid]: null }); }
  };

  const acceptCounter = async (leadId, driverId) => {
    setBusy({ ...busy, [`ctr_${driverId}`]: true });
    try {
      await api.post(`/admin/leads/${leadId}/counter/${driverId}/accept`);
      toast.success("Counter accepted — lead assigned & priced");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not accept counter"); }
    finally { setBusy({ ...busy, [`ctr_${driverId}`]: false }); }
  };

  const saveVt = async (vt) => {
    const edit = vtEdits[vt.id] || {};
    const payload = {
      name: edit.name ?? vt.name,
      base_fare: Number(edit.base_fare ?? vt.base_fare),
      per_km_rate: Number(edit.per_km_rate ?? vt.per_km_rate),
      round_trip_per_km: (edit.round_trip_per_km ?? vt.round_trip_per_km) ? Number(edit.round_trip_per_km ?? vt.round_trip_per_km) : null,
      round_trip_per_hour: (edit.round_trip_per_hour ?? vt.round_trip_per_hour) ? Number(edit.round_trip_per_hour ?? vt.round_trip_per_hour) : null,
      rental_rates: edit.rental_rates ?? vt.rental_rates ?? {},
      night_halt_rate: (edit.night_halt_rate ?? vt.night_halt_rate) ? Number(edit.night_halt_rate ?? vt.night_halt_rate) : null,
    };
    setBusy({ ...busy, [vt.id]: "vt" });
    try {
      await api.put(`/admin/vehicle-types/${vt.id}`, payload);
      toast.success("Pricing updated — applies to new bookings");
      await load();
    } catch (e) { toast.error("Could not update"); }
    finally { setBusy({ ...busy, [vt.id]: null }); }
  };

  const setRentalRate = (vt, pkg, val) => {
    const cur = vtEdits[vt.id]?.rental_rates ?? vt.rental_rates ?? {};
    const next = { ...cur };
    if (val === "" || Number(val) <= 0) delete next[pkg]; else next[pkg] = Number(val);
    setVtEdits({ ...vtEdits, [vt.id]: { ...vtEdits[vt.id], rental_rates: next } });
  };

  const toggleVt = async (vt) => {
    try { await api.put(`/admin/vehicle-types/${vt.id}`, { active: !vt.active }); await load(); }
    catch (e) { toast.error("Could not toggle"); }
  };

  const deleteVt = async (vt) => {
    setBusy({ ...busy, [vt.id]: "del" });
    try { await api.delete(`/admin/vehicle-types/${vt.id}`); toast.success("Vehicle type removed"); await load(); }
    catch (e) { toast.error("Could not delete"); }
    finally { setBusy({ ...busy, [vt.id]: null }); }
  };

  const addVt = async () => {
    if (!newVt.name || !newVt.base_fare || !newVt.per_km_rate) return toast.error("Fill name, base fare and per-km rate");
    setBusy({ ...busy, add_vt: true });
    try {
      await api.post("/admin/vehicle-types", {
        name: newVt.name, base_fare: Number(newVt.base_fare), per_km_rate: Number(newVt.per_km_rate),
        round_trip_per_km: newVt.round_trip_per_km ? Number(newVt.round_trip_per_km) : null,
        round_trip_per_hour: newVt.round_trip_per_hour ? Number(newVt.round_trip_per_hour) : null,
        night_halt_rate: newVt.night_halt_rate ? Number(newVt.night_halt_rate) : null,
      });
      toast.success("Vehicle type added");
      setNewVt({ name: "", base_fare: "", per_km_rate: "", round_trip_per_km: "", round_trip_per_hour: "", night_halt_rate: "" });
      await load();
    } catch (e) { toast.error("Could not add"); }
    finally { setBusy({ ...busy, add_vt: false }); }
  };

  const addDay = () => setNewItin((n) => ({ ...n, days: [...n.days, { day: n.days.length + 1, title: "", detail: "", photo_path: null }] }));
  const setDay = (i, k, val) => setNewItin((n) => ({ ...n, days: n.days.map((d, j) => j === i ? { ...d, [k]: val } : d) }));
  const rmDay = (i) => setNewItin((n) => ({ ...n, days: n.days.filter((_, j) => j !== i).map((d, j) => ({ ...d, day: j + 1 })) }));
  const setPaxRate = (vehicle, range, val) => setNewItin((n) => ({
    ...n, pax_rates: { ...n.pax_rates, [vehicle]: { ...(n.pax_rates[vehicle] || {}), [range]: val } },
  }));

  const uploadDayPhoto = async (i, file) => {
    if (!file) return;
    setBusy((b) => ({ ...b, [`day_photo_${i}`]: true }));
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/itineraries/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDay(i, "photo_path", r.data.photo_path);
      toast.success(`Day ${i + 1} photo uploaded`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Photo upload failed"); }
    finally { setBusy((b) => ({ ...b, [`day_photo_${i}`]: false })); }
  };

  const uploadCoverPhoto = async (file) => {
    if (!file) return;
    setBusy((b) => ({ ...b, cover_photo: true }));
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/itineraries/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setNewItin((n) => ({ ...n, cover_photo_path: r.data.photo_path }));
      toast.success("Cover photo uploaded");
    } catch (e) { toast.error(e?.response?.data?.detail || "Photo upload failed"); }
    finally { setBusy((b) => ({ ...b, cover_photo: false })); }
  };

  const addListItem = (key) => setNewItin((n) => ({ ...n, [key]: [...n[key], ""] }));
  const setListItem = (key, i, val) => setNewItin((n) => ({ ...n, [key]: n[key].map((x, j) => (j === i ? val : x)) }));
  const rmListItem = (key, i) => setNewItin((n) => ({ ...n, [key]: n[key].filter((_, j) => j !== i) }));

  const emptyItin = (kind) => ({ kind, title: "", destination: "", region: "", summary: "", cover_photo_path: null, price_from: "", days: [], inclusions: [], exclusions: [], pax_rates: {} });

  const editItin = (it) => {
    setNewItin({
      kind: it.kind, title: it.title || "", destination: it.destination || "", region: it.region || "", summary: it.summary || "",
      cover_photo_path: it.cover_photo_path || null,
      price_from: it.price_from ?? "",
      days: (it.days || []).map((d) => ({ day: d.day, title: d.title, detail: d.detail || "", photo_path: d.photo_path || null })),
      inclusions: [...(it.inclusions || [])],
      exclusions: [...(it.exclusions || [])],
      pax_rates: JSON.parse(JSON.stringify(it.pax_rates || {})),
    });
    setEditingItinId(it.id);
    document.getElementById("itin-builder")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const cancelEditItin = () => { setEditingItinId(null); setNewItin(emptyItin("cab_package")); };

  const saveItin = async () => {
    if (!newItin.title) return toast.error("Enter a title");
    if (!newItin.cover_photo_path) return toast.error("Upload a cover photo");
    const paxRates = {};
    Object.entries(newItin.pax_rates).forEach(([veh, ranges]) => {
      const cleaned = {};
      Object.entries(ranges).forEach(([rg, v]) => { if (v && Number(v) > 0) cleaned[rg] = Number(v); });
      if (Object.keys(cleaned).length) paxRates[veh] = cleaned;
    });
    const payload = {
      title: newItin.title, destination: newItin.destination, region: newItin.region, summary: newItin.summary,
      cover_photo_path: newItin.cover_photo_path,
      price_from: newItin.price_from ? Number(newItin.price_from) : null,
      days: newItin.days.filter((d) => d.title || d.photo_path).map((d) => ({ day: Number(d.day), title: d.title || `Day ${d.day}`, detail: d.detail, photo_path: d.photo_path || null })),
      inclusions: newItin.inclusions.map((x) => x.trim()).filter(Boolean),
      exclusions: newItin.exclusions.map((x) => x.trim()).filter(Boolean),
      pax_rates: paxRates,
    };
    setBusy({ ...busy, add_itin: true });
    try {
      if (editingItinId) {
        await api.put(`/admin/itineraries/${editingItinId}`, payload);
        toast.success("Package updated");
      } else {
        await api.post("/admin/itineraries", { kind: newItin.kind, ...payload });
        toast.success("Itinerary saved");
      }
      cancelEditItin();
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    finally { setBusy({ ...busy, add_itin: false }); }
  };

  const toggleItin = async (it) => {
    try { await api.put(`/admin/itineraries/${it.id}`, { active: !it.active }); await load(); }
    catch (e) { toast.error("Could not toggle"); }
  };

  const deleteItin = async (it) => {
    setBusy({ ...busy, [it.id]: "del" });
    try { await api.delete(`/admin/itineraries/${it.id}`); toast.success("Removed"); await load(); }
    catch (e) { toast.error("Could not delete"); }
    finally { setBusy({ ...busy, [it.id]: null }); }
  };

  const saveHillRate = async () => {
    const rates = {};
    vtypes.forEach((v) => { const val = newRate.rates[v.name]; if (val && Number(val) > 0) rates[v.name] = Number(val); });
    if (!newRate.from.trim() || !newRate.to.trim()) return toast.error("Enter From and To names");
    if (!Object.keys(rates).length) return toast.error("Enter at least one vehicle rate");
    setBusy({ ...busy, add_rate: true });
    try {
      await api.post("/admin/hill-rates", {
        from_names: newRate.from.split(",").map((s) => s.trim()).filter(Boolean),
        to_names: newRate.to.split(",").map((s) => s.trim()).filter(Boolean),
        rates,
      });
      toast.success("Hill Station rate saved");
      setNewRate({ from: "", to: "", rates: {} });
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save rate"); }
    finally { setBusy({ ...busy, add_rate: false }); }
  };

  const updateHillRate = async (r) => {
    const edit = rateEdits[r.id] || {};
    const rates = {};
    vtypes.forEach((v) => { const val = edit.rates?.[v.name] ?? r.rates?.[v.name]; if (val && Number(val) > 0) rates[v.name] = Number(val); });
    setBusy({ ...busy, [r.id]: "save" });
    try {
      await api.put(`/admin/hill-rates/${r.id}`, {
        from_names: (edit.from ?? r.from_names.join(", ")).split(",").map((s) => s.trim()).filter(Boolean),
        to_names: (edit.to ?? r.to_names.join(", ")).split(",").map((s) => s.trim()).filter(Boolean),
        rates,
        active: r.active,
      });
      toast.success("Rate updated");
      await load();
    } catch (e) { toast.error("Could not update rate"); }
    finally { setBusy({ ...busy, [r.id]: null }); }
  };

  const deleteHillRate = async (r) => {
    setBusy({ ...busy, [r.id]: "del" });
    try {
      await api.delete(`/admin/hill-rates/${r.id}`);
      toast.success("Rate deleted");
      await load();
    } catch (e) { toast.error("Could not delete"); }
    finally { setBusy({ ...busy, [r.id]: null }); }
  };

  const statCards = [
    { icon: TrendingUp, label: "Total Leads", value: stats.total_leads ?? 0, color: "text-slate-900 bg-slate-100" },
    { icon: Megaphone, label: "Broadcasted", value: stats.broadcast ?? 0, color: "text-indigo-600 bg-indigo-100" },
    { icon: CheckCircle2, label: "Accepted", value: stats.accepted ?? 0, color: "text-emerald-600 bg-emerald-100" },
    { icon: UserCheck, label: "Pending Drivers", value: stats.pending_drivers ?? 0, color: "text-amber-600 bg-amber-100" },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-40 backdrop-blur-md bg-white/90 border-b border-slate-200/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-slate-900 flex items-center justify-center">
              <Truck className="w-5 h-5 text-amber-500" />
            </div>
            <div>
              <span className="font-display text-lg font-extrabold text-slate-900">Admin Panel</span>
              <p className="text-xs text-slate-500 -mt-0.5">{user?.email}</p>
            </div>
          </div>
          <Button data-testid="admin-logout-button" variant="outline" onClick={doLogout} className="rounded-full border-slate-300">
            <LogOut className="w-4 h-4 mr-2" /> Logout
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {statCards.map((c) => (
            <div key={c.label} className="bg-white rounded-2xl border border-slate-200/80 p-5 shadow-[0_2px_8px_rgba(15,23,42,0.06)]">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${c.color}`}>
                <c.icon className="w-5 h-5" />
              </div>
              <p className="mt-3 font-display text-3xl font-extrabold text-slate-900">{c.value}</p>
              <p className="text-sm text-slate-500">{c.label}</p>
            </div>
          ))}
        </div>

        <AdminLivePanel leads={leads} onChanged={load} />

        <Tabs defaultValue="leads">
          <TabsList className="bg-white border border-slate-200 rounded-xl p-1 max-w-full overflow-x-auto justify-start">
            <TabsTrigger value="leads" data-testid="admin-tab-leads" className="rounded-lg data-[state=active]:bg-slate-900 data-[state=active]:text-white">Leads</TabsTrigger>
            <TabsTrigger value="drivers" data-testid="admin-tab-drivers" className="rounded-lg data-[state=active]:bg-slate-900 data-[state=active]:text-white">
              Drivers {stats.pending_drivers ? <span className="ml-2 rounded-full bg-amber-500 text-slate-900 text-xs px-1.5">{stats.pending_drivers}</span> : null}
            </TabsTrigger>
            <TabsTrigger value="customers" data-testid="admin-tab-customers" className="rounded-lg data-[state=active]:bg-slate-900 data-[state=active]:text-white">Customers</TabsTrigger>
            <TabsTrigger value="pricing" data-testid="admin-tab-pricing" className="rounded-lg data-[state=active]:bg-slate-900 data-[state=active]:text-white">Vehicle Pricing</TabsTrigger>
            <TabsTrigger value="hillrates" data-testid="admin-tab-hillrates" className="rounded-lg data-[state=active]:bg-slate-900 data-[state=active]:text-white">Hill Rates</TabsTrigger>
            <TabsTrigger value="itineraries" data-testid="admin-tab-itineraries" className="rounded-lg data-[state=active]:bg-slate-900 data-[state=active]:text-white">Itineraries</TabsTrigger>
          </TabsList>

          {/* LEADS */}
          <TabsContent value="leads" className="mt-6">
            <div className="bg-white rounded-2xl border border-slate-200/80 shadow-[0_2px_8px_rgba(15,23,42,0.06)] overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[960px]">
                  <thead>
                    <tr className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
                      <th className="py-3 px-5">Route / Trip</th>
                      <th className="py-3 px-5">Customer</th>
                      <th className="py-3 px-5">Received</th>
                      <th className="py-3 px-5">Offer</th>
                      <th className="py-3 px-5">Lead Price (₹)</th>
                      <th className="py-3 px-5">Status</th>
                      <th className="py-3 px-5">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leads.length === 0 && (<tr><td colSpan={7} className="py-12 text-center text-slate-400">No leads yet</td></tr>)}
                    {leads.map((l) => (
                      <tr key={l.id} data-testid="admin-lead-row" className="border-t border-slate-100 align-top">
                        <td className="py-4 px-5">
                          <div className="flex items-center gap-1.5 font-semibold text-slate-900">
                            {l.from_location} <ArrowRight className="w-3.5 h-3.5 text-amber-500" /> {l.to_location}
                          </div>
                          <span className="text-xs text-slate-400 flex items-center gap-1 mt-0.5">
                            <Clock className="w-3 h-3" /> {formatDateTime(l.travel_date, l.travel_time)} · {l.vehicle_type}
                            {l.distance_km ? ` · ~${l.distance_km} km` : ""}{l.passengers ? ` · ${l.passengers} pax` : ""}
                          </span>
                        </td>
                        <td className="py-4 px-5">
                          <p className="font-medium text-slate-800">{l.customer_name}</p>
                          {l.verified === false && (
                            <span data-testid="admin-lead-unverified-badge" className="inline-block mt-0.5 rounded-full bg-rose-100 border border-rose-200 px-2 py-0.5 text-[10px] font-semibold text-rose-700">Unverified</span>
                          )}
                          <p className="text-xs text-slate-400">+91 {l.customer_phone}</p>
                        </td>
                        <td className="py-4 px-5 text-xs text-slate-500 whitespace-nowrap">{formatTimestamp(l.created_at)}</td>
                        <td className="py-4 px-5 text-slate-700">{inr(l.fare_offer)}
                          {l.quoted_fare != null && <p data-testid="admin-lead-quoted-fare" className="text-[11px] text-emerald-600">Shown: {inr(l.quoted_fare)}</p>}
                        </td>
                        <td className="py-4 px-5">
                          <div className="flex items-center gap-2">
                            <div className="flex items-center rounded-md border border-input px-2 w-28">
                              <IndianRupee className="w-3.5 h-3.5 text-slate-400" />
                              <Input
                                data-testid="admin-lead-price-input"
                                type="number"
                                defaultValue={l.lead_price ?? ""}
                                onChange={(e) => setPriceEdits({ ...priceEdits, [l.id]: e.target.value })}
                                className="border-0 shadow-none focus-visible:ring-0 px-1 h-9"
                                placeholder="0"
                              />
                            </div>
                            <Button data-testid="admin-save-lead-price-button" size="sm" variant="outline" onClick={() => savePrice(l.id)} disabled={busy[l.id] === "price"} className="h-9 rounded-lg">Save</Button>
                          </div>
                          <div className="flex items-center rounded-md border border-input px-2 w-28 mt-1.5" title="Suggested fare shown to drivers after purchase. Auto-computed from vehicle rates if left blank.">
                            <IndianRupee className="w-3.5 h-3.5 text-slate-400" />
                            <Input
                              data-testid="admin-suggested-fare-input"
                              type="number"
                              key={`fare-${l.id}-${l.suggested_fare ?? "x"}`}
                              defaultValue={l.suggested_fare ?? ""}
                              onChange={(e) => setFareEdits({ ...fareEdits, [l.id]: e.target.value })}
                              className="border-0 shadow-none focus-visible:ring-0 px-1 h-8 text-xs"
                              placeholder="Suggested fare"
                            />
                          </div>
                        </td>
                        <td className="py-4 px-5">
                          <Badge className={`border ${STATUS_STYLE[l.status]} capitalize`}>{l.status}</Badge>
                          {l.accepted_by_name && (<p className="text-xs text-emerald-600 mt-1">by {l.accepted_by_name}</p>)}
                          {l.targeted && (l.assigned_drivers || []).length > 0 && (<p data-testid="admin-lead-targeted-note" className="text-xs text-emerald-700 font-semibold mt-1">Sent to {l.assigned_drivers.length} driver(s)</p>)}
                          {(l.counter_offers || []).length > 0 && (
                            <div className="mt-2 space-y-1.5">
                              {l.counter_offers.map((o) => (
                                <div key={o.driver_id} data-testid={`admin-counter-${o.driver_id}`} className="flex items-center gap-2 text-xs">
                                  <span className="text-slate-600">{o.driver_name}: <b>₹{Number(o.amount).toLocaleString("en-IN")}</b></span>
                                  {o.status === "pending" ? (
                                    <button
                                      data-testid={`admin-accept-counter-${o.driver_id}`}
                                      onClick={() => acceptCounter(l.id, o.driver_id)}
                                      disabled={busy[`ctr_${o.driver_id}`] || l.status === "accepted"}
                                      className="px-2 py-0.5 rounded-full bg-indigo-600 text-white font-semibold hover:bg-indigo-700 disabled:opacity-40"
                                    >
                                      Accept
                                    </button>
                                  ) : (
                                    <span className={`capitalize font-medium ${o.status === "accepted" ? "text-emerald-600" : "text-red-500"}`}>{o.status}</span>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </td>
                        <td className="py-4 px-5">
                          <div className="flex flex-col gap-1.5">
                            <button
                              data-testid="admin-premium-toggle" onClick={() => togglePremium(l)} disabled={busy[`prem_${l.id}`]}
                              title={l.premium ? "Unmark premium" : "Mark premium: city-wide, first buyer gets it exclusively"}
                              className={`h-8 rounded-lg border text-xs font-semibold flex items-center justify-center gap-1.5 ${l.premium ? "bg-amber-100 border-amber-300 text-amber-700" : "border-slate-200 text-slate-500 hover:border-amber-300"}`}
                            >
                              <Star className={`w-3.5 h-3.5 ${l.premium ? "fill-amber-500 text-amber-500" : ""}`} /> {l.premium ? "Premium" : "Mark Premium"}
                            </button>
                            <Button
                              data-testid="admin-broadcast-lead-button" size="sm" onClick={() => broadcast(l.id, false)}
                              disabled={busy[l.id] === "broadcast" || l.lead_price == null || l.status === "accepted"}
                              className="h-9 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white"
                            >
                              {busy[l.id] === "broadcast" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Radio className="w-4 h-4 mr-1.5" /> {l.broadcast ? "Re-broadcast" : "Broadcast"}</>}
                            </Button>
                            <Button
                              data-testid="admin-broadcast-all-button" size="sm" variant="outline" onClick={() => broadcast(l.id, true)}
                              disabled={busy[l.id] === "broadcast" || l.lead_price == null || l.status === "accepted"}
                              className="h-8 rounded-lg border-indigo-200 text-indigo-600 hover:bg-indigo-50 text-xs"
                            >
                              Broadcast to all
                            </Button>
                            <p data-testid="admin-broadcast-reach" className="text-[11px] text-slate-400 text-center">
                              Reaches <span className="font-semibold text-indigo-600">{broadcastReach(l)}</span> matching · <span className="font-semibold">{approvedDrivers}</span> total
                            </p>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* DRIVERS */}
          <TabsContent value="drivers" className="mt-6">
            <div className="mb-4 bg-white rounded-2xl border border-slate-200/80 p-5 shadow-[0_2px_8px_rgba(15,23,42,0.06)]">
              <h3 className="font-display font-bold text-slate-900 flex items-center gap-2"><Plus className="w-4 h-4 text-amber-500" /> Add Driver Manually</h3>
              <p className="text-sm text-slate-500 mt-1">Creates an approved driver instantly (no RC/DL needed). They can log in later via OTP with this phone.</p>
              <div className="mt-3 grid sm:grid-cols-2 lg:grid-cols-6 gap-2">
                <Input data-testid="add-driver-name" placeholder="Name" value={newDriver.name} onChange={(e) => setNewDriver({ ...newDriver, name: e.target.value })} />
                <Input data-testid="add-driver-phone" placeholder="Phone (10-digit)" value={newDriver.phone} onChange={(e) => setNewDriver({ ...newDriver, phone: e.target.value })} />
                <Input data-testid="add-driver-email" placeholder="Email (optional)" value={newDriver.email} onChange={(e) => setNewDriver({ ...newDriver, email: e.target.value })} />
                <Input data-testid="add-driver-city" placeholder="Cities (comma-separated, e.g. Kolkata, Siliguri)" value={newDriver.city} onChange={(e) => setNewDriver({ ...newDriver, city: e.target.value })} />
                <Input data-testid="add-driver-state" placeholder="State (optional)" value={newDriver.state} onChange={(e) => setNewDriver({ ...newDriver, state: e.target.value })} />
                <select data-testid="add-driver-vehicle" value={newDriver.vehicle_type} onChange={(e) => setNewDriver({ ...newDriver, vehicle_type: e.target.value })}
                  className="h-10 rounded-md border border-input bg-transparent px-2 text-sm">
                  <option value="">Vehicle type</option>
                  {vtypes.map((v) => <option key={v.id} value={v.name}>{v.name}</option>)}
                </select>
              </div>
              <Button data-testid="add-driver-button" onClick={addDriver} disabled={busy.add_driver}
                className="mt-3 h-10 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-semibold px-6">
                {busy.add_driver ? <Loader2 className="w-4 h-4 animate-spin" /> : "Add Driver"}
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {drivers.length === 0 && (
                <div className="col-span-full py-12 text-center text-slate-400 bg-white rounded-2xl border border-slate-200/80">No driver registrations yet</div>
              )}
              {drivers.map((d) => (
                <div key={d.user_id} data-testid="admin-driver-card" className="bg-white rounded-2xl border border-slate-200/80 p-5 shadow-[0_2px_8px_rgba(15,23,42,0.06)]">
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-full bg-slate-100 flex items-center justify-center font-display font-bold text-slate-700">
                      {d.name?.[0]?.toUpperCase() || "D"}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-slate-900 truncate flex items-center gap-1.5">
                        <span data-testid="admin-driver-online-dot" title={d.online ? "Online" : "Offline"} className={`inline-block w-2.5 h-2.5 rounded-full ${d.online ? "bg-emerald-500" : "bg-slate-300"}`} />
                        {d.name}
                      </p>
                      <p className="text-xs text-slate-400 truncate">{d.email}</p>
                    </div>
                  </div>
                  <div className="mt-4 space-y-1.5 text-sm">
                    <div className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2">
                      <span className="text-slate-600"><span className="text-slate-400">Wallet:</span> <span data-testid="admin-driver-wallet-balance" className="font-bold text-slate-900">₹{Number(d.wallet_balance || 0).toLocaleString("en-IN")}</span></span>
                      <button data-testid={`admin-driver-wallet-button-${d.user_id}`} onClick={() => setWalletDriver(d)} className="text-xs font-semibold text-amber-600 hover:underline">Manage wallet</button>
                    </div>
                    <p className="text-slate-600"><span className="text-slate-400">Phone:</span> {d.phone ? `+91 ${d.phone}` : "—"}</p>
                    <p className="text-slate-600"><span className="text-slate-400">Vehicle:</span> {d.vehicle_type || "—"}</p>
                    <div className="text-slate-600 flex items-center gap-2 flex-wrap">
                      <span><span className="text-slate-400">Cities:</span> {(d.cities?.length ? d.cities : (d.city ? [d.city] : [])).join(", ") || "—"}{d.manual_add ? <span className="ml-2 text-xs text-indigo-500">· manually added</span> : null}</span>
                      {editingCity === d.user_id ? (
                        <span className="flex items-center gap-1.5">
                          <Input data-testid={`admin-city-input-${d.user_id}`} value={cityValue} onChange={(e) => setCityValue(e.target.value)} placeholder="Cities, comma-sep" className="h-7 w-36 text-xs" />
                          <Button data-testid={`admin-save-city-${d.user_id}`} size="sm" onClick={() => saveDriverCity(d)} disabled={busy[`city_${d.user_id}`]} className="h-7 rounded-lg px-2 text-xs bg-emerald-600 hover:bg-emerald-700 text-white">
                            {busy[`city_${d.user_id}`] ? <Loader2 className="w-3 h-3 animate-spin" /> : "Save"}
                          </Button>
                          <button data-testid={`admin-cancel-city-${d.user_id}`} onClick={() => setEditingCity(null)} className="text-slate-400 hover:text-red-500"><X className="w-4 h-4" /></button>
                        </span>
                      ) : (
                        <button data-testid={`admin-edit-city-${d.user_id}`} onClick={() => { setEditingCity(d.user_id); setCityValue((d.cities?.length ? d.cities : (d.city ? [d.city] : [])).join(", ")); }} className="text-xs font-medium text-amber-600 hover:underline">Edit</button>
                      )}
                    </div>
                  </div>

                  {/* Documents */}
                  <div className="mt-4 grid grid-cols-2 gap-2">
                    <DocButton label="RC" has={d.has_rc} userId={d.user_id} docType="rc" />
                    <DocButton label="DL" has={d.has_dl} userId={d.user_id} docType="dl" />
                  </div>

                  <div className="mt-4 flex items-center justify-between gap-2">
                    <Badge className={`border capitalize ${DRIVER_STATUS_STYLE[d.driver_status]}`}>{d.driver_status}</Badge>
                    <div className="flex gap-2">
                      {d.driver_status !== "approved" && (
                        <Button
                          data-testid="admin-approve-driver-button" size="sm"
                          onClick={() => setDriverStatus(d.user_id, "approved")}
                          disabled={busy[d.user_id]}
                          title={d.registration_complete ? "Approve driver" : `Missing: ${(d.missing_fields || []).join(", ")}`}
                          className="h-9 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-40"
                        >
                          <CheckCircle2 className="w-4 h-4 mr-1" /> Approve
                        </Button>
                      )}
                      {d.driver_status !== "rejected" && (
                        <Button
                          data-testid="admin-reject-driver-button" size="sm" variant="outline"
                          onClick={() => setDriverStatus(d.user_id, "rejected")} disabled={busy[d.user_id]}
                          className="h-9 rounded-lg border-red-200 text-red-600 hover:bg-red-50"
                        >
                          <XCircle className="w-4 h-4 mr-1" /> Reject
                        </Button>
                      )}
                    </div>
                  </div>
                  {(d.missing_fields || []).length > 0 && (
                    <p data-testid="admin-driver-missing-fields" className="mt-2 text-xs text-amber-600 flex items-center gap-1">
                      <FileWarning className="w-3.5 h-3.5" /> Missing: {d.missing_fields.join(", ")}
                    </p>
                  )}
                </div>
              ))}
            </div>
            {walletDriver && <DriverWalletDialog driver={walletDriver} onClose={() => setWalletDriver(null)} onChanged={load} />}
          </TabsContent>

          {/* PRICING */}
          {/* CUSTOMERS */}
          <TabsContent value="customers" className="mt-6 space-y-4">
            <div className="bg-white rounded-2xl border border-slate-200/80 p-5 shadow-[0_2px_8px_rgba(15,23,42,0.06)]">
              <h3 className="font-display font-bold text-slate-900 flex items-center gap-2"><Plus className="w-4 h-4 text-amber-500" /> Add Customer Manually</h3>
              <p className="text-sm text-slate-500 mt-1">Register an offline customer, then create enquiries on their behalf.</p>
              <div className="mt-3 grid sm:grid-cols-4 gap-2">
                <Input data-testid="add-customer-name" placeholder="Name" value={newCustomer.name} onChange={(e) => setNewCustomer({ ...newCustomer, name: e.target.value })} />
                <Input data-testid="add-customer-phone" placeholder="Phone (10-digit)" value={newCustomer.phone} onChange={(e) => setNewCustomer({ ...newCustomer, phone: e.target.value })} />
                <Input data-testid="add-customer-email" placeholder="Email (optional)" value={newCustomer.email} onChange={(e) => setNewCustomer({ ...newCustomer, email: e.target.value })} />
                <Button data-testid="add-customer-button" onClick={addCustomer} disabled={busy.add_customer} className="h-10 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-semibold">
                  {busy.add_customer ? <Loader2 className="w-4 h-4 animate-spin" /> : "Add Customer"}
                </Button>
              </div>
            </div>

            {customers.length === 0 && <div className="bg-white rounded-2xl border border-dashed border-slate-300 p-10 text-center text-slate-400">No customers yet</div>}
            {customers.map((c) => (
              <div key={c.user_id} data-testid="customer-card" className={`bg-white rounded-2xl border p-5 shadow-[0_2px_8px_rgba(15,23,42,0.06)] ${highlightCust === c.user_id ? "border-amber-400 ring-2 ring-amber-200" : "border-slate-200/80"}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <p className="font-display font-bold text-slate-900">{c.name}</p>
                    <p className="text-sm text-slate-500 mt-0.5">{c.phone ? `+91 ${c.phone}` : "—"}{c.email ? ` · ${c.email}` : ""}</p>
                    <p className="text-xs text-slate-400 mt-0.5">{c.enquiries.length} enquir{c.enquiries.length === 1 ? "y" : "ies"}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button data-testid={`customer-edit-button-${c.user_id}`} size="sm" variant="outline"
                      onClick={() => { setEditCust(editCust === c.user_id ? null : c.user_id); setCustEdit({ name: c.name || "", phone: c.phone || "", email: c.email || "" }); setEnqCust(null); }}
                      className="h-8 rounded-lg border-amber-200 text-amber-600 hover:bg-amber-50"><Pencil className="w-3.5 h-3.5 mr-1" /> Edit</Button>
                    <Button data-testid={`customer-enquiry-button-${c.user_id}`} size="sm" variant="outline"
                      onClick={() => { setEnqCust(enqCust === c.user_id ? null : c.user_id); setEditCust(null); }}
                      className="h-8 rounded-lg border-indigo-200 text-indigo-600 hover:bg-indigo-50"><Plus className="w-3.5 h-3.5 mr-1" /> New Enquiry</Button>
                  </div>
                </div>

                {editCust === c.user_id && (
                  <div data-testid={`customer-edit-form-${c.user_id}`} className="mt-3 grid sm:grid-cols-4 gap-2 rounded-xl bg-slate-50 p-3">
                    <Input data-testid="edit-customer-name" placeholder="Name" value={custEdit.name} onChange={(e) => setCustEdit({ ...custEdit, name: e.target.value })} />
                    <Input data-testid="edit-customer-phone" placeholder="Phone" value={custEdit.phone} onChange={(e) => setCustEdit({ ...custEdit, phone: e.target.value })} />
                    <Input data-testid="edit-customer-email" placeholder="Email" value={custEdit.email} onChange={(e) => setCustEdit({ ...custEdit, email: e.target.value })} />
                    <div className="flex gap-2">
                      <Button data-testid="edit-customer-save" size="sm" onClick={() => saveCustomer(c)} disabled={busy[`cust_${c.user_id}`]} className="h-9 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white">
                        {busy[`cust_${c.user_id}`] ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setEditCust(null)} className="h-9 rounded-lg">Cancel</Button>
                    </div>
                  </div>
                )}

                {enqCust === c.user_id && (
                  <div data-testid={`customer-enquiry-form-${c.user_id}`} className="mt-3 rounded-xl bg-indigo-50/60 border border-indigo-100 p-3 space-y-2">
                    <p className="text-xs font-semibold text-indigo-700 uppercase tracking-wider">Create enquiry on behalf (offline → online)</p>
                    <div className="grid sm:grid-cols-3 gap-2">
                      <Input data-testid="cust-enq-from" placeholder="From (pickup city)" value={enqForm.from_location} onChange={(e) => setEnqForm({ ...enqForm, from_location: e.target.value })} />
                      <Input data-testid="cust-enq-to" placeholder="To (destination)" value={enqForm.to_location} onChange={(e) => setEnqForm({ ...enqForm, to_location: e.target.value })} />
                      <select data-testid="cust-enq-vehicle" value={enqForm.vehicle_type} onChange={(e) => setEnqForm({ ...enqForm, vehicle_type: e.target.value })}
                        className="h-10 rounded-md border border-input bg-transparent px-2 text-sm">
                        <option value="">Vehicle type</option>
                        {vtypes.map((v) => <option key={v.id} value={v.name}>{v.name}</option>)}
                      </select>
                      <Input data-testid="cust-enq-date" type="date" value={enqForm.travel_date} onChange={(e) => setEnqForm({ ...enqForm, travel_date: e.target.value })} />
                      <Input data-testid="cust-enq-time" type="time" value={enqForm.travel_time} onChange={(e) => setEnqForm({ ...enqForm, travel_time: e.target.value })} />
                      <Input data-testid="cust-enq-offer" type="number" placeholder="Offer rate ₹ (optional)" value={enqForm.fare_offer} onChange={(e) => setEnqForm({ ...enqForm, fare_offer: e.target.value })} />
                      <select data-testid="cust-enq-triptype" value={enqForm.trip_type} onChange={(e) => setEnqForm({ ...enqForm, trip_type: e.target.value })}
                        className="h-10 rounded-md border border-input bg-transparent px-2 text-sm">
                        {["One Way", "Round Trip", "Rental", "Hill Station"].map((t) => <option key={t} value={t}>{t}</option>)}
                      </select>
                      <Input data-testid="cust-enq-stopovers" placeholder="Stopovers (comma-separated, optional)" value={enqForm.stopovers} onChange={(e) => setEnqForm({ ...enqForm, stopovers: e.target.value })} className="sm:col-span-3" />
                    </div>
                    <Button data-testid="cust-enq-submit" size="sm" onClick={() => createEnquiry(c)} disabled={busy[`enq_${c.user_id}`]} className="h-9 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white">
                      {busy[`enq_${c.user_id}`] ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create Enquiry"}
                    </Button>
                    <p className="text-[11px] text-indigo-400">Created as a new lead — set its price and broadcast from the Leads tab.</p>
                  </div>
                )}

                {c.enquiries.length > 0 && (
                  <div className="mt-3">
                    <button data-testid={`customer-enquiries-toggle-${c.user_id}`} onClick={() => setExpandedCust(expandedCust === c.user_id ? null : c.user_id)} className="text-xs font-medium text-slate-500 hover:text-slate-700">
                      {expandedCust === c.user_id ? "Hide" : "Show"} {c.enquiries.length} enquir{c.enquiries.length === 1 ? "y" : "ies"}
                    </button>
                    {expandedCust === c.user_id && (
                      <div className="mt-2 space-y-2">
                        {c.enquiries.map((e) => (
                          <div key={e.id} data-testid="customer-enquiry-row" className="rounded-xl border border-slate-100 px-3 py-2">
                            <div className="flex items-center justify-between gap-2 flex-wrap">
                              <span className="text-sm font-medium text-slate-800 flex items-center gap-1">{e.from_location} <ArrowRight className="w-3.5 h-3.5 text-amber-500" /> {e.to_location}</span>
                              <Badge className={`border capitalize ${STATUS_STYLE[e.status] || "bg-slate-100 text-slate-600 border-slate-200"}`}>{e.status}</Badge>
                            </div>
                            <p className="text-xs text-slate-400 mt-0.5">{formatDateTime(e.travel_date, e.travel_time)} · {e.trip_type} · {e.vehicle_type}{e.passengers ? ` · ${e.passengers} pax` : ""}{e.fare_offer ? ` · offer ${inr(e.fare_offer)}` : ""}</p>
                            {e.driver_name && (
                              <p data-testid="customer-assigned-driver" className="text-xs text-emerald-700 mt-1 font-medium">Driver: {e.driver_name}{e.driver_phone ? ` · +91 ${e.driver_phone}` : ""}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </TabsContent>

          <TabsContent value="pricing" className="mt-6">
            <div className="bg-white rounded-2xl border border-slate-200/80 shadow-[0_2px_8px_rgba(15,23,42,0.06)] p-5">
              <h3 className="font-display font-bold text-slate-900 flex items-center gap-2"><Car className="w-5 h-5 text-amber-500" /> Manage Vehicle Types & Pricing</h3>
              <p className="text-sm text-slate-500 mt-1">Set a base flat rate and a per-km rate for each type. Changes apply immediately to new bookings.</p>

              <div className="mt-5 overflow-x-auto">
                <table className="w-full min-w-[720px]">
                  <thead>
                    <tr className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
                      <th className="py-3 px-4">Vehicle Type</th>
                      <th className="py-3 px-4">Base Fare (₹)</th>
                      <th className="py-3 px-4">Per-km Rate (₹)</th>
                      <th className="py-3 px-4">Round-Trip ₹/km</th>
                      <th className="py-3 px-4">Night Halt ₹/night</th>
                      <th className="py-3 px-4">RT ₹/hr</th>
                      <th className="py-3 px-4">Active</th>
                      <th className="py-3 px-4">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {vtypes.map((vt) => (
                      <tr key={vt.id} data-testid="admin-vehicle-type-row" className="border-t border-slate-100">
                        <td className="py-3 px-4">
                          <Input
                            data-testid="admin-vt-name-input"
                            defaultValue={vt.name}
                            onChange={(e) => setVtEdits({ ...vtEdits, [vt.id]: { ...vtEdits[vt.id], name: e.target.value } })}
                            className="h-9 w-40"
                          />
                        </td>
                        <td className="py-3 px-4">
                          <Input
                            data-testid="admin-vt-base-input" type="number" defaultValue={vt.base_fare}
                            onChange={(e) => setVtEdits({ ...vtEdits, [vt.id]: { ...vtEdits[vt.id], base_fare: e.target.value } })}
                            className="h-9 w-28"
                          />
                        </td>
                        <td className="py-3 px-4">
                          <Input
                            data-testid="admin-vt-perkm-input" type="number" defaultValue={vt.per_km_rate}
                            onChange={(e) => setVtEdits({ ...vtEdits, [vt.id]: { ...vtEdits[vt.id], per_km_rate: e.target.value } })}
                            className="h-9 w-28"
                          />
                        </td>
                        <td className="py-3 px-4">
                          <Input
                            data-testid="admin-vt-roundtrip-input" type="number" defaultValue={vt.round_trip_per_km ?? ""} placeholder={vt.per_km_rate}
                            onChange={(e) => setVtEdits({ ...vtEdits, [vt.id]: { ...vtEdits[vt.id], round_trip_per_km: e.target.value } })}
                            className="h-9 w-28"
                          />
                        </td>
                        <td className="py-3 px-4">
                          <Input
                            data-testid="admin-vt-nighthalt-input" type="number" defaultValue={vt.night_halt_rate ?? ""} placeholder="0"
                            onChange={(e) => setVtEdits({ ...vtEdits, [vt.id]: { ...vtEdits[vt.id], night_halt_rate: e.target.value } })}
                            className="h-9 w-28"
                          />
                        </td>
                        <td className="py-3 px-4">
                          <Input
                            data-testid="admin-vt-rthour-input" type="number" defaultValue={vt.round_trip_per_hour ?? ""} placeholder="0"
                            onChange={(e) => setVtEdits({ ...vtEdits, [vt.id]: { ...vtEdits[vt.id], round_trip_per_hour: e.target.value } })}
                            className="h-9 w-28"
                          />
                        </td>
                        <td className="py-3 px-4">
                          <button
                            data-testid="admin-vt-toggle" onClick={() => toggleVt(vt)}
                            className={`px-3 py-1 rounded-full text-xs font-semibold border ${vt.active ? "bg-emerald-100 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-500 border-slate-200"}`}
                          >
                            {vt.active ? "Active" : "Hidden"}
                          </button>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex gap-2">
                            <Button data-testid="admin-vt-save-button" size="sm" onClick={() => saveVt(vt)} disabled={busy[vt.id] === "vt"} className="h-9 rounded-lg bg-slate-900 hover:bg-slate-800 text-white">
                              {busy[vt.id] === "vt" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1" /> Save</>}
                            </Button>
                            <Button data-testid="admin-vt-delete-button" size="sm" variant="outline" onClick={() => deleteVt(vt)} disabled={busy[vt.id] === "del"} className="h-9 rounded-lg border-red-200 text-red-600 hover:bg-red-50">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {/* Add new */}
                    <tr className="border-t border-slate-100 bg-amber-50/40">
                      <td className="py-3 px-4">
                        <Input data-testid="admin-new-vt-name" placeholder="e.g. Bike" value={newVt.name} onChange={(e) => setNewVt({ ...newVt, name: e.target.value })} className="h-9 w-40" />
                      </td>
                      <td className="py-3 px-4">
                        <Input data-testid="admin-new-vt-base" type="number" placeholder="Base" value={newVt.base_fare} onChange={(e) => setNewVt({ ...newVt, base_fare: e.target.value })} className="h-9 w-28" />
                      </td>
                      <td className="py-3 px-4">
                        <Input data-testid="admin-new-vt-perkm" type="number" placeholder="Per km" value={newVt.per_km_rate} onChange={(e) => setNewVt({ ...newVt, per_km_rate: e.target.value })} className="h-9 w-28" />
                      </td>
                      <td className="py-3 px-4">
                        <Input data-testid="admin-new-vt-roundtrip" type="number" placeholder="RT /km" value={newVt.round_trip_per_km} onChange={(e) => setNewVt({ ...newVt, round_trip_per_km: e.target.value })} className="h-9 w-28" />
                      </td>
                      <td className="py-3 px-4">
                        <Input data-testid="admin-new-vt-nighthalt" type="number" placeholder="Night halt" value={newVt.night_halt_rate} onChange={(e) => setNewVt({ ...newVt, night_halt_rate: e.target.value })} className="h-9 w-28" />
                      </td>
                      <td className="py-3 px-4">
                        <Input data-testid="admin-new-vt-rthour" type="number" placeholder="RT /hr" value={newVt.round_trip_per_hour} onChange={(e) => setNewVt({ ...newVt, round_trip_per_hour: e.target.value })} className="h-9 w-28" />
                      </td>
                      <td className="py-3 px-4 text-xs text-slate-400">—</td>
                      <td className="py-3 px-4">
                        <Button data-testid="admin-add-vt-button" size="sm" onClick={addVt} disabled={busy.add_vt} className="h-9 rounded-lg bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold">
                          {busy.add_vt ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1" /> Add</>}
                        </Button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="px-5 pb-5 pt-2 border-t border-slate-100">
                <h4 className="font-semibold text-slate-800 text-sm mb-1">Rental Package Rates</h4>
                <p className="text-xs text-slate-400 mb-3">Fixed price per vehicle for each rental package. Leave blank if a vehicle isn't offered for rentals. Click Save on the vehicle row above to persist.</p>
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[560px] text-sm">
                    <thead><tr className="bg-slate-50 text-xs text-slate-500 text-left">
                      <th className="py-2 px-3">Vehicle</th>
                      {RENTAL_PACKAGES.map((p) => <th key={p} className="py-2 px-3">{p}</th>)}
                      <th className="py-2 px-3"></th>
                    </tr></thead>
                    <tbody>
                      {vtypes.map((vt) => (
                        <tr key={vt.id} className="border-t border-slate-100">
                          <td className="py-2 px-3 font-medium text-slate-700 whitespace-nowrap">{vt.name}</td>
                          {RENTAL_PACKAGES.map((p) => (
                            <td key={p} className="py-2 px-3">
                              <div className="flex items-center rounded-md border border-input px-1.5 w-28">
                                <IndianRupee className="w-3 h-3 text-slate-400" />
                                <Input data-testid={`admin-rental-${vt.name.replace(/\s/g, "-")}-${p.replace(/[^a-zA-Z0-9]/g, "")}`} type="number" placeholder="0"
                                  value={(vtEdits[vt.id]?.rental_rates ?? vt.rental_rates ?? {})[p] || ""}
                                  onChange={(e) => setRentalRate(vt, p, e.target.value)}
                                  className="border-0 shadow-none focus-visible:ring-0 px-1 h-8 text-xs" />
                              </div>
                            </td>
                          ))}
                          <td className="py-2 px-3">
                            <Button data-testid={`admin-rental-save-${vt.id}`} size="sm" onClick={() => saveVt(vt)} disabled={busy[vt.id] === "vt"} className="h-8 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-xs">
                              {busy[vt.id] === "vt" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Save"}
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* HILL STATION RATES */}
          <TabsContent value="hillrates" className="mt-6">
            <div className="bg-white rounded-2xl border border-slate-200/80 shadow-[0_2px_8px_rgba(15,23,42,0.06)] overflow-hidden">
              <div className="px-5 pt-5 pb-2">
                <h3 className="font-display font-bold text-slate-900">Hill Station Fixed Rates</h3>
                <p className="text-sm text-slate-500 mt-0.5">One fixed price per vehicle for each hill route. From/To accept comma-separated aliases (e.g. From: Bagdogra, Siliguri, NJP → To: Darjeeling). Customers whose GPS From/To match a route see these rates during enquiry.</p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[760px]">
                  <thead>
                    <tr className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
                      <th className="py-3 px-4">From (aliases)</th>
                      <th className="py-3 px-4">To (aliases)</th>
                      {vtypes.map((v) => <th key={v.id} className="py-3 px-4">{v.name}</th>)}
                      <th className="py-3 px-4">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {hillRates.map((r) => {
                      const edit = rateEdits[r.id] || {};
                      return (
                        <tr key={r.id} data-testid="hill-rate-row" className="border-t border-slate-100 align-top">
                          <td className="py-3 px-4"><Input data-testid="hill-rate-from-input" defaultValue={r.from_names.join(", ")} onChange={(e) => setRateEdits({ ...rateEdits, [r.id]: { ...edit, from: e.target.value } })} className="h-9 w-48" /></td>
                          <td className="py-3 px-4"><Input data-testid="hill-rate-to-input" defaultValue={r.to_names.join(", ")} onChange={(e) => setRateEdits({ ...rateEdits, [r.id]: { ...edit, to: e.target.value } })} className="h-9 w-40" /></td>
                          {vtypes.map((v) => (
                            <td key={v.id} className="py-3 px-4">
                              <div className="flex items-center rounded-md border border-input px-2 w-24">
                                <IndianRupee className="w-3.5 h-3.5 text-slate-400" />
                                <Input data-testid={`hill-rate-price-${v.name.replace(/\s/g, "-")}`} type="number" defaultValue={r.rates?.[v.name] ?? ""} placeholder="—"
                                  onChange={(e) => setRateEdits({ ...rateEdits, [r.id]: { ...edit, rates: { ...(edit.rates || {}), [v.name]: e.target.value } } })}
                                  className="border-0 shadow-none focus-visible:ring-0 px-1 h-9" />
                              </div>
                            </td>
                          ))}
                          <td className="py-3 px-4 whitespace-nowrap">
                            <Button data-testid="hill-rate-save-button" size="sm" variant="outline" onClick={() => updateHillRate(r)} disabled={busy[r.id] === "save"} className="h-9 rounded-lg mr-2">Save</Button>
                            <Button data-testid="hill-rate-delete-button" size="sm" variant="outline" onClick={() => deleteHillRate(r)} disabled={busy[r.id] === "del"} className="h-9 rounded-lg border-red-200 text-red-600 hover:bg-red-50"><Trash2 className="w-4 h-4" /></Button>
                          </td>
                        </tr>
                      );
                    })}
                    <tr className="border-t border-slate-100 bg-amber-50/40">
                      <td className="py-3 px-4"><Input data-testid="hill-rate-new-from" placeholder="Bagdogra, Siliguri, NJP" value={newRate.from} onChange={(e) => setNewRate({ ...newRate, from: e.target.value })} className="h-9 w-48" /></td>
                      <td className="py-3 px-4"><Input data-testid="hill-rate-new-to" placeholder="Darjeeling" value={newRate.to} onChange={(e) => setNewRate({ ...newRate, to: e.target.value })} className="h-9 w-40" /></td>
                      {vtypes.map((v) => (
                        <td key={v.id} className="py-3 px-4">
                          <div className="flex items-center rounded-md border border-input px-2 w-24">
                            <IndianRupee className="w-3.5 h-3.5 text-slate-400" />
                            <Input data-testid={`hill-rate-new-price-${v.name.replace(/\s/g, "-")}`} type="number" placeholder="0" value={newRate.rates[v.name] || ""}
                              onChange={(e) => setNewRate({ ...newRate, rates: { ...newRate.rates, [v.name]: e.target.value } })}
                              className="border-0 shadow-none focus-visible:ring-0 px-1 h-9" />
                          </div>
                        </td>
                      ))}
                      <td className="py-3 px-4">
                        <Button data-testid="hill-rate-add-button" size="sm" onClick={saveHillRate} disabled={busy.add_rate} className="h-9 rounded-lg bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold">
                          {busy.add_rate ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1" /> Add</>}
                        </Button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* ITINERARIES */}
          <TabsContent value="itineraries" className="mt-6 grid lg:grid-cols-2 gap-6">
            {/* Builder */}
            <div id="itin-builder" className="bg-white rounded-2xl border border-slate-200/80 shadow-[0_2px_8px_rgba(15,23,42,0.06)] p-5">
              <div className="flex items-start justify-between gap-2">
                <h3 className="font-display font-bold text-slate-900 flex items-center gap-2"><Package className="w-5 h-5 text-amber-500" /> {editingItinId ? "Edit Cab Package" : "New Cab Package Itinerary"}</h3>
                {editingItinId && (
                  <Button data-testid="itin-cancel-edit-button" size="sm" variant="outline" onClick={cancelEditItin} className="h-8 rounded-lg border-slate-200 text-slate-500">Cancel</Button>
                )}
              </div>
              <p className="text-sm text-slate-500 mt-1">Build a Cab Package with a day-wise plan. Hill Station bookings are plain From → To trips priced by admin vehicle rates.</p>
              <div className="mt-3 space-y-3">
                <Input data-testid="itin-title-input" placeholder="Title (e.g. Manali Adventure 4D/3N)" value={newItin.title} onChange={(e) => setNewItin({ ...newItin, title: e.target.value })} />
                <Input data-testid="itin-destination-input" placeholder="Destination (e.g. Manali, Himachal Pradesh)" value={newItin.destination} onChange={(e) => setNewItin({ ...newItin, destination: e.target.value })} />
                <div>
                  <Label className="text-sm font-medium">Cover Photo <span className="text-red-500">*</span></Label>
                  <div className="mt-1.5 flex items-center gap-3">
                    {newItin.cover_photo_path ? (
                      <img src={`${API_ROOT}/itinerary-photo?path=${encodeURIComponent(newItin.cover_photo_path)}`} alt="cover" data-testid="itin-cover-preview" className="w-28 h-16 rounded-lg object-cover border border-slate-200" />
                    ) : (
                      <div className="w-28 h-16 rounded-lg border border-dashed border-slate-300 flex items-center justify-center text-slate-300"><ImageIcon className="w-6 h-6" /></div>
                    )}
                    <label className="text-sm font-medium text-amber-600 cursor-pointer hover:underline">
                      {busy.cover_photo ? "Uploading…" : (newItin.cover_photo_path ? "Change cover photo" : "Upload cover photo")}
                      <input data-testid="itin-cover-input" type="file" accept="image/*" className="hidden" onChange={(e) => uploadCoverPhoto(e.target.files?.[0])} />
                    </label>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Input data-testid="itin-region-input" placeholder="Region / State" value={newItin.region} onChange={(e) => setNewItin({ ...newItin, region: e.target.value })} />
                  <div className="flex items-center rounded-md border border-input px-2"><IndianRupee className="w-3.5 h-3.5 text-slate-400" /><Input data-testid="itin-price-input" type="number" placeholder="Price from" value={newItin.price_from} onChange={(e) => setNewItin({ ...newItin, price_from: e.target.value })} className="border-0 shadow-none focus-visible:ring-0 px-1" /></div>
                </div>
                <Input data-testid="itin-summary-input" placeholder="Short summary" value={newItin.summary} onChange={(e) => setNewItin({ ...newItin, summary: e.target.value })} />
                <div>
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-medium">Day-wise Plan</Label>
                    <Button data-testid="itin-add-day-button" size="sm" variant="outline" onClick={addDay} className="h-8 rounded-lg"><Plus className="w-4 h-4 mr-1" /> Add Day</Button>
                  </div>
                  <div className="mt-2 space-y-2">
                    {newItin.days.map((d, i) => (
                      <div key={i} data-testid={`itin-day-row-${i}`} className="rounded-xl border border-slate-200 p-2.5">
                        <div className="flex items-center gap-2">
                          <span className="w-8 h-8 shrink-0 rounded-lg bg-slate-900 text-white text-xs font-bold flex items-center justify-center">D{d.day}</span>
                          <Input data-testid={`itin-day-title-${i}`} placeholder="Day title (e.g. Arrival & Local Sightseeing)" value={d.title} onChange={(e) => setDay(i, "title", e.target.value)} className="h-9" />
                          <button onClick={() => rmDay(i)} className="text-slate-400 hover:text-red-500"><X className="w-4 h-4" /></button>
                        </div>
                        <Input data-testid={`itin-day-detail-${i}`} placeholder="Details (optional)" value={d.detail} onChange={(e) => setDay(i, "detail", e.target.value)} className="h-9 mt-2" />
                        <div className="mt-2 flex items-center gap-2">
                          {d.photo_path ? (
                            <img src={`${API_ROOT}/itinerary-photo?path=${encodeURIComponent(d.photo_path)}`} alt={`Day ${d.day}`} data-testid={`itin-day-photo-preview-${i}`} className="w-14 h-14 rounded-lg object-cover border border-slate-200" />
                          ) : (
                            <div className="w-14 h-14 rounded-lg border border-dashed border-slate-300 flex items-center justify-center text-slate-300"><ImageIcon className="w-5 h-5" /></div>
                          )}
                          <label className="text-xs font-medium text-amber-600 cursor-pointer hover:underline">
                            {busy[`day_photo_${i}`] ? "Uploading…" : (d.photo_path ? "Change photo" : "Upload day photo")}
                            <input data-testid={`itin-day-photo-input-${i}`} type="file" accept="image/*" className="hidden" onChange={(e) => uploadDayPhoto(i, e.target.files?.[0])} />
                          </label>
                        </div>
                      </div>
                    ))}
                    {newItin.days.length === 0 && <p className="text-xs text-slate-400">No days added yet.</p>}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <div className="flex items-center justify-between">
                      <Label className="text-sm font-medium text-emerald-700">Inclusions</Label>
                      <Button data-testid="itin-add-inclusion-button" size="sm" variant="outline" onClick={() => addListItem("inclusions")} className="h-7 rounded-lg px-2"><Plus className="w-3.5 h-3.5" /></Button>
                    </div>
                    <div className="mt-1.5 space-y-1.5">
                      {newItin.inclusions.map((x, i) => (
                        <div key={i} className="flex items-center gap-1.5">
                          <Input data-testid={`itin-inclusion-${i}`} placeholder="e.g. Hotel stay" value={x} onChange={(e) => setListItem("inclusions", i, e.target.value)} className="h-8 text-xs" />
                          <button onClick={() => rmListItem("inclusions", i)} className="text-slate-400 hover:text-red-500"><X className="w-4 h-4" /></button>
                        </div>
                      ))}
                      {newItin.inclusions.length === 0 && <p className="text-xs text-slate-400">None added.</p>}
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center justify-between">
                      <Label className="text-sm font-medium text-red-600">Exclusions</Label>
                      <Button data-testid="itin-add-exclusion-button" size="sm" variant="outline" onClick={() => addListItem("exclusions")} className="h-7 rounded-lg px-2"><Plus className="w-3.5 h-3.5" /></Button>
                    </div>
                    <div className="mt-1.5 space-y-1.5">
                      {newItin.exclusions.map((x, i) => (
                        <div key={i} className="flex items-center gap-1.5">
                          <Input data-testid={`itin-exclusion-${i}`} placeholder="e.g. Air fare" value={x} onChange={(e) => setListItem("exclusions", i, e.target.value)} className="h-8 text-xs" />
                          <button onClick={() => rmListItem("exclusions", i)} className="text-slate-400 hover:text-red-500"><X className="w-4 h-4" /></button>
                        </div>
                      ))}
                      {newItin.exclusions.length === 0 && <p className="text-xs text-slate-400">None added.</p>}
                    </div>
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-medium">Rates by Vehicle × Pax</Label>
                  <p className="text-xs text-slate-400 mb-2">Set the total package price per vehicle for each passenger count. Customer's rate = vehicle + their pax count.</p>
                  <div className="overflow-x-auto rounded-lg border border-slate-200">
                    <table className="w-full text-sm">
                      <thead><tr className="bg-slate-50 text-xs text-slate-500">
                        <th className="py-2 px-2 text-left">Vehicle</th>
                        {PAX_OPTIONS.map((rg) => <th key={rg} className="py-2 px-2">{rg} pax</th>)}
                      </tr></thead>
                      <tbody>
                        {vtypes.map((v) => (
                          <tr key={v.id} className="border-t border-slate-100">
                            <td className="py-2 px-2 font-medium text-slate-700 whitespace-nowrap">{v.name}</td>
                            {PAX_OPTIONS.map((rg) => (
                              <td key={rg} className="py-2 px-2">
                                <div className="flex items-center rounded-md border border-input px-1.5 w-20">
                                  <IndianRupee className="w-3 h-3 text-slate-400" />
                                  <Input data-testid={`itin-paxrate-${v.name.replace(/\s/g, "-")}-${rg}`} type="number" placeholder="0"
                                    value={newItin.pax_rates[v.name]?.[rg] || ""} onChange={(e) => setPaxRate(v.name, rg, e.target.value)}
                                    className="border-0 shadow-none focus-visible:ring-0 px-0.5 h-8 text-xs" />
                                </div>
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                <Button data-testid="itin-save-button" onClick={saveItin} disabled={busy.add_itin} className="w-full h-11 rounded-xl bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold">
                  {busy.add_itin ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Save className="w-4 h-4 mr-1" /> {editingItinId ? "Update Package" : "Save Itinerary"}</>}
                </Button>
              </div>
            </div>

            {/* List */}
            <div className="space-y-3">
              {itins.filter((it) => it.kind === "cab_package").length === 0 && <div className="bg-white rounded-2xl border border-dashed border-slate-300 p-10 text-center text-slate-400">No cab package itineraries yet</div>}
              {itins.filter((it) => it.kind === "cab_package").map((it) => (
                <div key={it.id} data-testid="admin-itinerary-card" className="bg-white rounded-2xl border border-slate-200/80 shadow-[0_2px_8px_rgba(15,23,42,0.06)] p-5">
                  {it.cover_photo_path && (
                    <img src={`${API_ROOT}/itinerary-photo?path=${encodeURIComponent(it.cover_photo_path)}`} alt={it.title} data-testid="admin-itinerary-cover" className="w-full aspect-[16/9] object-cover rounded-xl border border-slate-200 mb-3" />
                  )}
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <Package className="w-4 h-4 text-indigo-500" />
                        <span className="font-display font-bold text-slate-900">{it.title}</span>
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5">{it.destination || it.region || "—"} · {it.days?.length || 0} days{it.price_from ? ` · from ${inr(it.price_from)}` : ""}</p>
                      {it.summary && <p className="text-sm text-slate-600 mt-1">{it.summary}</p>}
                      {(it.inclusions?.length > 0 || it.exclusions?.length > 0) && (
                        <p className="text-xs text-slate-400 mt-1">{it.inclusions?.length || 0} inclusions · {it.exclusions?.length || 0} exclusions</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Button data-testid={`itin-edit-button-${it.id}`} size="sm" variant="outline" onClick={() => editItin(it)} className="h-8 rounded-lg border-amber-200 text-amber-600 hover:bg-amber-50"><Pencil className="w-4 h-4 mr-1" /> Edit</Button>
                      <button data-testid="itin-toggle" onClick={() => toggleItin(it)} className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${it.active ? "bg-emerald-100 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-500 border-slate-200"}`}>{it.active ? "Active" : "Hidden"}</button>
                      <Button data-testid="itin-delete-button" size="sm" variant="outline" onClick={() => deleteItin(it)} disabled={busy[it.id] === "del"} className="h-8 rounded-lg border-red-200 text-red-600 hover:bg-red-50"><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </div>
                  {it.days?.length > 0 && (
                    <ol className="mt-3 space-y-1.5">
                      {it.days.map((d) => (
                        <li key={d.day} className="flex gap-2 text-sm">
                          <span className="w-7 h-7 shrink-0 rounded-lg bg-slate-100 text-slate-700 text-xs font-bold flex items-center justify-center">D{d.day}</span>
                          <div><p className="font-medium text-slate-800">{d.title}</p>{d.detail && <p className="text-xs text-slate-500">{d.detail}</p>}</div>
                        </li>
                      ))}
                    </ol>
                  )}
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

function DocButton({ label, has, userId, docType }) {
  if (!has) {
    return (
      <div className="flex items-center justify-center gap-1.5 rounded-lg border border-dashed border-slate-200 px-3 py-2 text-xs text-slate-400">
        <FileWarning className="w-3.5 h-3.5" /> {label} missing
      </div>
    );
  }
  return (
    <a
      data-testid={`admin-view-${docType}-${userId}`}
      href={`${API}/documents/${userId}/${docType}`}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center justify-center gap-1.5 rounded-lg border border-slate-200 px-3 py-2 text-xs font-medium text-slate-700 hover:bg-amber-50 hover:border-amber-300 transition-colors"
    >
      <FileText className="w-3.5 h-3.5 text-amber-600" /> View {label}
    </a>
  );
}
